import { logger } from "./logger.js";
import { extractPageContext } from "./page-context.js";
import { uniqueToolId } from "./tool-name.js";
import type { ChatConfig, ChatConfigProvider } from "./types/config.js";
import type { PageContext, UserIdentity } from "./types/messaging.js";
import type { ToolCallHandler, ToolDefinition } from "./types/tools.js";
import {
  handleRealtimeMessage,
  INITIAL_VOICE_MACHINE,
  type RealtimeMessageContext,
  type RealtimeResponseUsage,
  type VoiceMachine,
  type VoiceTranscriptEvent,
  voiceReducer,
} from "./voice-machine.js";
import { type VoiceTranscriptMessage, VoiceTranscriptSequencer } from "./voice-transcript.js";

const DEFAULT_REALTIME_MODEL = "gpt-realtime";
const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const DEFAULT_API_ORIGIN = "https://chat.yak.io";

// Local development flag, set by yak's own apps to point voice sessions at a
// chat UI running on localhost.
declare global {
  interface Window {
    __YAK_INTERNAL_DEV__?: boolean;
  }
}

/**
 * Resolves the API origin when no explicit `apiOrigin` is configured. Points at
 * a local chat UI during yak's own local development; production otherwise.
 */
function getDefaultApiOrigin(): string {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
    typeof window.__YAK_INTERNAL_DEV__ !== "undefined"
  ) {
    return "http://localhost:3001";
  }
  return DEFAULT_API_ORIGIN;
}

export type VoiceStateListener = (machine: VoiceMachine) => void;

export interface YakVoiceSessionConfig {
  appId: string;
  /** Tool call handler. Same shape as `YakClientConfig.onToolCall`. */
  onToolCall?: ToolCallHandler;
  onRedirect?: (path: string) => void;
  /**
   * Static chat config (routes + tools). Sent to the mint endpoint so the LLM
   * knows what tools are available. Use this OR `getConfig`.
   */
  chatConfig?: ChatConfig;
  /**
   * Async provider for chat config. Called on every session start — useful when
   * tools/routes depend on the current page or user. Takes precedence over
   * `chatConfig` if both are provided.
   */
  getConfig?: ChatConfigProvider;
  /**
   * Override the API origin (where voice sessions are minted). Defaults to
   * `https://chat.yak.io`. Most integrators never set this.
   */
  apiOrigin?: string;
  /**
   * Privacy opt-out: stop sending any page context (URL, title, visible text)
   * with the voice session. The session still works; the assistant just won't
   * be aware of the current page.
   */
  disablePageContent?: boolean;
  /**
   * Signed end-user identity. Same shape and contract as `YakClientConfig.user`:
   * when supplied, the voice session persists its transcript server-side keyed
   * to this user (so insights + memory work) and the model recalls the user's
   * memory at session start. The `hash` must be HMAC-SHA256(apiSecret, id)
   * computed on the integrator's backend — never expose `apiSecret` to the
   * browser.
   */
  user?: UserIdentity;
}

interface MintResponse {
  clientSecret: string;
  expiresAt: number;
  voiceSessionId: string;
  /**
   * Whether to auto-greet on connect. Driven by the app's voice intro setting:
   * `false` only when the operator chose "no intro". A missing flag (older
   * server) is treated as `true` so existing apps keep greeting.
   */
  autoGreet?: boolean;
}

interface SessionResources {
  pc: RTCPeerConnection | null;
  dataChannel: RTCDataChannel | null;
  micStream: MediaStream | null;
  audioElement: HTMLAudioElement | null;
  voiceSessionId: string | null;
}

const EMPTY_RESOURCES: SessionResources = {
  pc: null,
  dataChannel: null,
  micStream: null,
  audioElement: null,
  voiceSessionId: null,
};

interface AccumulatedUsage {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  audioInputTokens: number;
  audioOutputTokens: number;
  textInputTokens: number;
  textOutputTokens: number;
  responseCount: number;
}

function emptyUsage(): AccumulatedUsage {
  return {
    inputTokens: 0,
    cachedInputTokens: 0,
    outputTokens: 0,
    audioInputTokens: 0,
    audioOutputTokens: 0,
    textInputTokens: 0,
    textOutputTokens: 0,
    responseCount: 0,
  };
}

/**
 * Manages a single voice session against the OpenAI Realtime API.
 *
 * Runs entirely on the host page — no iframe. The mint endpoint
 * (`POST /api/voice/realtime-token`) is called cross-origin to chat.yak.io;
 * origin validation happens server-side via the app's `allowedOrigins`.
 *
 * Tool calls are dispatched to the configured handlers — the same ones
 * customers wire up for `YakEmbed`. The class manages WebRTC lifecycle,
 * full teardown on stop, and `pagehide` cleanup via `navigator.sendBeacon`.
 */
export class YakVoiceSession {
  private config: YakVoiceSessionConfig;
  private machine: VoiceMachine = INITIAL_VOICE_MACHINE;
  private resources: SessionResources = EMPTY_RESOURCES;
  private dispatchedCallIds = new Set<string>();
  private listeners = new Set<VoiceStateListener>();
  private pageHideHandler: (() => void) | null = null;
  /** Per-session token totals, accumulated from each `response.done` event. */
  private usage: AccumulatedUsage = emptyUsage();
  /**
   * Reverse map: decorated (sanitized) model-facing tool id (what OpenAI calls
   * back with) → original host tool name (what `onToolCall` expects). Populated
   * on every `start()` from the resolved chat config.
   */
  private toolNameById = new Map<string, string>();

  // ── Transcript persistence ────────────────────────────────────────────────
  /** Assigns each settled transcript a stable order key and tracks unacked sends. */
  private transcript = new VoiceTranscriptSequencer();
  /** Latched once the server says this session won't persist (storage off / no row). */
  private persistenceOff = false;
  /** At most one persist POST in flight; a newer flush coalesces behind it. */
  private persistInFlight: Promise<void> | null = null;
  private persistAgain = false;
  /**
   * The voice session id used for transcript persistence. Mirrors
   * `resources.voiceSessionId` but is kept separate so a final flush during
   * `teardown()` (which clears `resources`) can still address the session.
   */
  private persistSessionId: string | null = null;

  constructor(config: YakVoiceSessionConfig) {
    this.config = config;
    this.attachPageHide();
  }

  /**
   * Resolve the API origin lazily on each call. Environment-dependent defaults
   * (e.g. a local chat UI) may not be ready at construction time, so resolving
   * eagerly would risk baking in the production URL.
   */
  private get apiOrigin(): string {
    return this.config.apiOrigin ?? getDefaultApiOrigin();
  }

  /** Update mutable config fields (handlers, getConfig). */
  public updateConfig(patch: Partial<YakVoiceSessionConfig>): void {
    this.config = { ...this.config, ...patch };
  }

  public getState(): VoiceMachine {
    return this.machine;
  }

  /**
   * The current API origin (defaults to `https://chat.yak.io`). Useful for
   * building URLs to static assets like the brand logo.
   */
  public getApiOrigin(): string {
    return this.apiOrigin;
  }

  public onStateChange(listener: VoiceStateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Begin a voice session. Should be invoked from a user gesture (button
   * click) so `getUserMedia` and audio playback both have transient activation.
   */
  public async start(): Promise<void> {
    if (this.machine.state !== "idle") return;
    logger.debug("Voice: start() called");
    this.usage = emptyUsage();
    this.resetTranscriptState();
    this.dispatch({ type: "start" });

    let chatConfig: ChatConfig | undefined = this.config.chatConfig;
    if (this.config.getConfig) {
      try {
        chatConfig = await this.config.getConfig();
        logger.debug("Voice: getConfig() resolved", {
          toolCount: chatConfig?.tools?.tools.length ?? 0,
          routeCount: chatConfig?.routes?.routes.length ?? 0,
        });
      } catch (err) {
        logger.warn("Voice: getConfig() failed", err);
      }
    } else if (chatConfig) {
      logger.debug("Voice: using static chatConfig", {
        toolCount: chatConfig.tools?.tools.length ?? 0,
        routeCount: chatConfig.routes?.routes.length ?? 0,
      });
    } else {
      logger.debug("Voice: no chatConfig or getConfig — only built-in tools will be available");
    }

    // Decorate host tools with sanitized, collision-free model-facing ids and
    // build the reverse lookup so we can map id-named tool calls back to the
    // original host name when the model invokes them. Mirrors the chat-ui
    // iframe's decoration step.
    const decoratedManifest = this.buildDecoratedManifest(chatConfig);
    logger.debug("Voice: decorated tools", {
      ids: decoratedManifest.tools.map((t) => `${t.id}=${t.name}`),
    });

    const pageContext = this.safeExtractPageContext();
    logger.debug("Voice: page context extracted", {
      url: pageContext?.url,
      title: pageContext?.title,
      textLength: pageContext?.text?.length ?? 0,
    });

    let mint: MintResponse;
    try {
      logger.debug("Voice: requesting ephemeral token from mint endpoint");
      mint = await this.mintToken(chatConfig, decoratedManifest, pageContext);
      logger.debug("Voice: mint succeeded", {
        voiceSessionId: mint.voiceSessionId,
        expiresAt: mint.expiresAt,
      });
    } catch (err) {
      await this.failWith(err instanceof Error ? err.message : "Failed to start voice session");
      return;
    }

    let micStream: MediaStream;
    try {
      logger.debug("Voice: requesting microphone access");
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      logger.debug("Voice: microphone access granted");
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      const message =
        name === "NotAllowedError" || name === "PermissionDeniedError"
          ? "Microphone permission was denied. Enable microphone access in your browser settings to use voice mode."
          : "Could not access microphone.";
      await this.failWith(message);
      return;
    }

    const pc = new RTCPeerConnection();
    const audioElement = document.createElement("audio");
    audioElement.autoplay = true;
    audioElement.style.display = "none";
    document.body.appendChild(audioElement);

    pc.ontrack = (event) => {
      logger.debug("Voice: pc.ontrack received remote audio stream");
      if (event.streams[0]) {
        audioElement.srcObject = event.streams[0];
      }
    };

    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      logger.debug("Voice: ICE connection state →", s);
      if (s === "failed" || s === "disconnected") {
        void this.failWith(`WebRTC connection ${s}`);
      }
    };
    pc.onconnectionstatechange = () => {
      logger.debug("Voice: peer connection state →", pc.connectionState);
      if (pc.connectionState === "failed") {
        void this.failWith("WebRTC connection failed");
      }
    };

    for (const track of micStream.getAudioTracks()) {
      pc.addTrack(track, micStream);
    }

    const dataChannel = pc.createDataChannel("oai-events");
    dataChannel.onmessage = (event) => {
      const raw = typeof event.data === "string" ? event.data : "";
      if (!raw) return;
      logger.debug("Voice: ← data channel message", raw.slice(0, 200));
      void handleRealtimeMessage(raw, this.buildMessageContext());
    };
    dataChannel.onopen = () => {
      logger.debug("Voice: data channel opened");
      this.dispatch({ type: "connected" });
      // Kick off an opening greeting so the assistant speaks first instead of
      // waiting silently for the user. The minted session already carries the
      // full instructions (persona, governance, language, "first turn" rule),
      // so a bare `response.create` is enough — do NOT attach response-level
      // instructions here, which would replace the session instructions.
      // Skip it when the app's voice intro is "none" (`autoGreet === false`);
      // a missing flag defaults to greeting for back-compat.
      if (mint.autoGreet !== false) {
        this.dispatch({ type: "response_requested" });
        this.sendOverDataChannel({ type: "response.create" });
      }
    };
    dataChannel.onclose = () => {
      logger.debug("Voice: data channel closed");
    };

    this.resources = {
      pc,
      dataChannel,
      micStream,
      audioElement,
      voiceSessionId: mint.voiceSessionId,
    };
    this.persistSessionId = mint.voiceSessionId;

    try {
      logger.debug("Voice: creating WebRTC offer");
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      logger.debug("Voice: exchanging SDP with OpenAI Realtime");
      const answerSdp = await this.exchangeSdp(offer, mint.clientSecret);
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      logger.debug("Voice: WebRTC negotiation complete");
    } catch (err) {
      await this.failWith(
        err instanceof Error ? err.message : "Failed to negotiate voice connection"
      );
      return;
    }

    void this.postSessionEvent("start", mint.voiceSessionId, pageContext);
  }

  /** Stop the session and tear down all resources. */
  public async stop(): Promise<void> {
    logger.debug("Voice: stop() called");
    this.dispatch({ type: "stop" });
    await this.teardown();
  }

  /** Tear down everything and remove listeners. Call once before discarding the instance. */
  public destroy(): void {
    void this.teardown();
    if (this.pageHideHandler) {
      window.removeEventListener("pagehide", this.pageHideHandler);
      this.pageHideHandler = null;
    }
    this.listeners.clear();
  }

  // ── Internals ───────────────────────────────────────────────────────────

  private buildMessageContext(): RealtimeMessageContext {
    return {
      send: (event) => this.dispatch(event),
      sendData: (payload) => this.sendOverDataChannel(payload),
      dispatchToolCall: (name, args) => this.routeToolCall(name, args),
      isDispatched: (id) => this.dispatchedCallIds.has(id),
      markDispatched: (id) => {
        this.dispatchedCallIds.add(id);
      },
      recordUsage: (usage) => this.accumulateUsage(usage),
      onTranscriptEvent: (event) => this.handleTranscriptEvent(event),
    };
  }

  private accumulateUsage(usage: RealtimeResponseUsage): void {
    this.usage.responseCount += 1;
    if (typeof usage.inputTokens === "number") this.usage.inputTokens += usage.inputTokens;
    if (typeof usage.cachedInputTokens === "number") {
      this.usage.cachedInputTokens += usage.cachedInputTokens;
    }
    if (typeof usage.outputTokens === "number") this.usage.outputTokens += usage.outputTokens;
    if (typeof usage.audioInputTokens === "number") {
      this.usage.audioInputTokens += usage.audioInputTokens;
    }
    if (typeof usage.audioOutputTokens === "number") {
      this.usage.audioOutputTokens += usage.audioOutputTokens;
    }
    if (typeof usage.textInputTokens === "number") {
      this.usage.textInputTokens += usage.textInputTokens;
    }
    if (typeof usage.textOutputTokens === "number") {
      this.usage.textOutputTokens += usage.textOutputTokens;
    }
  }

  private resetTranscriptState(): void {
    this.transcript = new VoiceTranscriptSequencer();
    this.persistenceOff = false;
    this.persistInFlight = null;
    this.persistAgain = false;
    this.persistSessionId = null;
  }

  /**
   * Drive the sequencer from a transcript event, then flush. A `user_turn`
   * reserves an ordering slot (its transcript completes asynchronously and can
   * land after the assistant has replied); a failed transcription releases that
   * slot; a settled `transcript` becomes a message to append. Each settled
   * message triggers a flush of everything still unacked.
   */
  private handleTranscriptEvent(event: VoiceTranscriptEvent): void {
    switch (event.kind) {
      case "user_turn":
        // Stamp the boundary time now — well before the user's transcript text
        // settles — so the message sorts by when the user actually spoke.
        this.transcript.userTurn(Date.now());
        return;
      case "user_transcript_failed":
        this.transcript.userTranscriptFailed();
        return;
      case "transcript":
        if (this.transcript.record(event.itemId, event.role, event.text, Date.now())) {
          this.flushTranscripts();
        }
        return;
      default: {
        const _exhaustive: never = event;
        void _exhaustive;
      }
    }
  }

  /**
   * Send everything still unacked as one additive batch. The server mints each
   * message's id from its boundary `ts` (idempotent, K-sortable) and creates the
   * conversation on the first user turn — so a re-sent batch is a safe no-op, and
   * the unacked set doubles as the retry + greeting-defer + `pagehide`-beacon
   * queue. Normally the batch is a single just-settled message.
   */
  private flushTranscripts(useBeacon = false): void {
    if (this.persistenceOff || !this.persistSessionId) return;
    const messages = this.transcript.pending();
    if (messages.length === 0) return;
    if (useBeacon) {
      this.beaconTranscript(messages);
      return;
    }
    void this.persistMessages(messages);
  }

  private buildTranscriptBody(messages: VoiceTranscriptMessage[]): string {
    return JSON.stringify({
      appId: this.config.appId,
      voiceSessionId: this.persistSessionId,
      messages,
    });
  }

  private async persistMessages(messages: VoiceTranscriptMessage[]): Promise<void> {
    if (this.persistInFlight) {
      // Coalesce: a newer flush runs once the in-flight POST settles.
      this.persistAgain = true;
      return;
    }
    const itemIds = messages.map((m) => m.itemId);
    const run = async (): Promise<void> => {
      try {
        const res = await fetch(`${this.apiOrigin}/api/voice/transcript`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: this.buildTranscriptBody(messages),
        });
        if (res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            persisted?: boolean;
            disabled?: boolean;
          };
          if (body.disabled) {
            // Storage off for this app — stop trying for the rest of the session.
            this.persistenceOff = true;
          } else if (body.persisted) {
            this.transcript.ack(itemIds);
          }
          // Otherwise the batch was deferred (a greeting before any user turn);
          // it stays unacked and rides along with the next flush.
        } else if (res.status === 401 || res.status === 403 || res.status === 404) {
          // Session not recognised / not permitted — don't hammer the endpoint.
          this.persistenceOff = true;
        }
      } catch (err) {
        logger.warn("Voice: failed to persist transcript", err);
      }
    };
    this.persistInFlight = run().finally(() => {
      this.persistInFlight = null;
      if (this.persistAgain) {
        this.persistAgain = false;
        this.flushTranscripts();
      }
    });
  }

  private beaconTranscript(messages: VoiceTranscriptMessage[]): void {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) return;
    try {
      navigator.sendBeacon(
        `${this.apiOrigin}/api/voice/transcript`,
        new Blob([this.buildTranscriptBody(messages)], { type: "application/json" })
      );
    } catch (err) {
      logger.warn("Voice: failed to beacon transcript", err);
    }
  }

  private sendOverDataChannel(payload: unknown): void {
    const channel = this.resources.dataChannel;
    if (!channel || channel.readyState !== "open") {
      logger.warn("Voice data channel not ready; dropping payload");
      return;
    }
    try {
      const serialized = JSON.stringify(payload);
      logger.debug("Voice: → data channel send", serialized.slice(0, 200));
      channel.send(serialized);
    } catch (err) {
      logger.warn("Failed to send on voice data channel", err);
    }
  }

  private async routeToolCall(idOrName: string, args: unknown): Promise<unknown> {
    // The model calls us back using the decorated id (e.g. orders_list).
    // Resolve it to the original host tool name; fall back to the raw value
    // (it might be `redirect` or some non-decorated name).
    const name = this.toolNameById.get(idOrName) ?? idOrName;
    logger.debug("Voice: tool call dispatched", { id: idOrName, name, args });
    // MCP tools execute server-side (the org token never reaches the browser).
    // The model calls back with the `mcp__…` name minted by the server; relay
    // it to the exec endpoint and feed the result back over the data channel.
    if (name.startsWith("mcp__")) {
      return await this.execMcpTool(name, args);
    }
    if (name === "redirect") {
      const path = (args as { path?: unknown })?.path;
      if (typeof path !== "string") {
        throw new Error("redirect tool requires a string `path` argument");
      }
      if (this.config.onRedirect) {
        this.config.onRedirect(path);
      } else if (typeof window !== "undefined") {
        window.location.assign(path);
      }
      return { success: true, redirected: true, path };
    }
    if (this.config.onToolCall) {
      return await this.config.onToolCall(name, args);
    }
    throw new Error(`No handler configured for tool: ${name}`);
  }

  /**
   * Relay an MCP tool call to the server, which holds the org's credentials
   * and executes against the remote MCP server. The browser only ever passes
   * through the tool name, args, and the opaque result.
   */
  private async execMcpTool(toolName: string, args: unknown): Promise<unknown> {
    try {
      const res = await fetch(`${this.apiOrigin}/api/voice/mcp-exec`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          appId: this.config.appId,
          toolName,
          args: args ?? {},
          pageContext: this.safeExtractPageContext(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        return { error: body.error ?? `MCP tool failed (${res.status})` };
      }
      const body = (await res.json()) as { result?: unknown };
      return body.result ?? {};
    } catch (err) {
      // The relay failed, but we hand a structured `{ error }` straight back to
      // the model to recover from — expected data flow, not a console-worthy
      // fault. Log at debug to match the text-chat tool-call path (client.ts).
      logger.debug("Voice: MCP tool relay failed", err);
      return { error: "The integration could not complete this request." };
    }
  }

  private async mintToken(
    chatConfig: ChatConfig | undefined,
    decoratedManifest: { tools: Array<ToolDefinition & { id: string }> },
    pageContext: PageContext | undefined
  ): Promise<MintResponse> {
    const res = await fetch(`${this.apiOrigin}/api/voice/realtime-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        appId: this.config.appId,
        pageContext,
        toolManifest: decoratedManifest,
        routeManifest: chatConfig?.routes,
        user: this.config.user,
      }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Mint failed (${res.status})`);
    }
    return (await res.json()) as MintResponse;
  }

  /**
   * Decorate the host's tool manifest with readable, collision-free model-facing ids
   * and populate `this.toolNameById` for reverse lookup. Mirrors the decoration the
   * chat-ui iframe applies before sending tools to `/api/chat`. GraphQL/REST tools are
   * ordinary manifest entries here (contributed by their adapters), so no special-casing
   * is needed.
   */
  private buildDecoratedManifest(chatConfig: ChatConfig | undefined): {
    tools: Array<ToolDefinition & { id: string }>;
  } {
    this.toolNameById.clear();
    const used = new Set<string>(["redirect"]);

    const decoratedHostTools = (chatConfig?.tools?.tools ?? []).map((t: ToolDefinition) => {
      const id = uniqueToolId(t.name, used);
      used.add(id);
      this.toolNameById.set(id, t.name);
      return { ...t, id };
    });

    return { tools: decoratedHostTools };
  }

  private async exchangeSdp(
    offer: RTCSessionDescriptionInit,
    clientSecret: string
  ): Promise<string> {
    const sdpResponse = await fetch(`${REALTIME_CALLS_URL}?model=${DEFAULT_REALTIME_MODEL}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        "Content-Type": "application/sdp",
      },
      body: offer.sdp,
    });
    if (!sdpResponse.ok) {
      const body = await sdpResponse.text().catch(() => "");
      throw new Error(`SDP exchange failed (${sdpResponse.status}): ${body}`);
    }
    return await sdpResponse.text();
  }

  private buildStopEventBody(voiceSessionId: string, pageContext: PageContext | undefined) {
    return {
      appId: this.config.appId,
      voiceSessionId,
      event: "stop" as const,
      clientTimestamp: Date.now(),
      pageContext,
      usage: { ...this.usage },
    };
  }

  private async postSessionEvent(
    event: "start" | "stop",
    voiceSessionId: string,
    pageContext: PageContext | undefined
  ): Promise<void> {
    try {
      const body =
        event === "stop"
          ? this.buildStopEventBody(voiceSessionId, pageContext)
          : {
              appId: this.config.appId,
              voiceSessionId,
              event,
              clientTimestamp: Date.now(),
              pageContext,
            };
      await fetch(`${this.apiOrigin}/api/voice/session-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: event === "stop",
      });
    } catch (err) {
      logger.warn(`Failed to post voice.session.${event}`, err);
    }
  }

  private async teardown(): Promise<void> {
    // Final transcript flush while the session id is still addressable (a POST
    // already in flight will re-run via the coalescing tail). persistSessionId
    // outlives the resources clear below, so this is safe to call first.
    this.flushTranscripts();

    const r = this.resources;
    this.resources = EMPTY_RESOURCES;
    this.dispatchedCallIds = new Set();

    try {
      r.dataChannel?.close();
    } catch (err) {
      logger.warn("Error closing data channel", err);
    }
    try {
      for (const track of r.micStream?.getTracks() ?? []) {
        track.stop();
      }
    } catch (err) {
      logger.warn("Error stopping mic tracks", err);
    }
    try {
      for (const sender of r.pc?.getSenders() ?? []) {
        sender.track?.stop();
      }
      r.pc?.close();
    } catch (err) {
      logger.warn("Error closing peer connection", err);
    }
    if (r.audioElement) {
      try {
        r.audioElement.srcObject = null;
        r.audioElement.remove();
      } catch (err) {
        logger.warn("Error removing audio element", err);
      }
    }

    if (r.voiceSessionId) {
      await this.postSessionEvent("stop", r.voiceSessionId, this.safeExtractPageContext());
    }
  }

  private async failWith(message: string): Promise<void> {
    logger.warn("Voice session error:", message);
    this.dispatch({ type: "error", message });
    await this.teardown();
  }

  private dispatch(event: Parameters<typeof voiceReducer>[1]): void {
    const next = voiceReducer(this.machine, event);
    if (next === this.machine) return;
    this.machine = next;
    for (const listener of this.listeners) {
      try {
        listener(next);
      } catch (err) {
        logger.warn("Voice state listener threw", err);
      }
    }
  }

  private safeExtractPageContext(): PageContext | undefined {
    // Privacy opt-out: never read or transmit page context for this app.
    if (this.config.disablePageContent) return undefined;
    try {
      return extractPageContext();
    } catch {
      return undefined;
    }
  }

  private attachPageHide(): void {
    if (typeof window === "undefined") return;
    this.pageHideHandler = () => {
      const r = this.resources;
      if (!r.voiceSessionId) return;
      // Beacon the final transcript turn before the stop event, so a tab close
      // mid-conversation doesn't drop the last exchange.
      this.flushTranscripts(true);
      const body = JSON.stringify(this.buildStopEventBody(r.voiceSessionId, undefined));
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${this.apiOrigin}/api/voice/session-event`,
          new Blob([body], { type: "application/json" })
        );
      }
    };
    window.addEventListener("pagehide", this.pageHideHandler);
  }
}
